import { Box, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { BffWorkingHours, Facility } from '@/types/generated';
import { addressLinkStyles, officeHoursButtonContainerStyles, weekendAvailabilityStyles, workingHoursBoxStyles, workingHoursContainerStyles } from './OfficeInfo.styles';
import OfficeHours from '@shared-components/components/Hero/ODP/OfficeHours';
import ReviewRating from '@shared-components/components/ReviewRating';
import TAGButton from '@shared-components/components/TAGButton';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { useAppContext, useFacilityContext } from '@shared-components/context';
import { Maybe } from '@shared-components/types';
import { getWorkingHours, getWorkingHoursFromBff, groupDays, resolveContentVariable } from '@utils';
type WorkingHoursDisplay = ReturnType<typeof getWorkingHours>;
const getWeekendAvailabilityMessage = (workingHours: WorkingHoursDisplay) => {
  const openWeekendDays = workingHours.filter(({
    day,
    hours
  }) => (day === 'Saturday' || day === 'Sunday') && hours !== 'Closed').map(({
    day
  }) => day);
  if (openWeekendDays.length === 2) return 'Open Saturday & Sunday';
  if (openWeekendDays.length === 1) return `Open ${openWeekendDays[0]}`;
  return null;
};
export interface IOfficeInfo {
  googleMapsBusinessQuery?: Maybe<string>;
  displayOfficeHoursInModal?: Maybe<boolean>;
  officeHoursLabel?: string;
  isDarkBackground?: boolean;
}
const OfficeInfo = ({
  googleMapsBusinessQuery,
  displayOfficeHoursInModal = false,
  officeHoursLabel,
  isDarkBackground = false
}: IOfficeInfo) => {
  const {
    officeInfo
  } = useFacilityContext(true);
  const {
    config
  } = useAppContext();
  const useFacilityBeffeDataSource = config.featureFlags.useFacilityBeffeDataSource;
  const [showHoursModal, setShowHoursModal] = useState(false);
  if (!officeInfo) return null;
  const address1 = officeInfo.address?.address1 as string;
  const address2 = officeInfo.address?.address2 as string;
  const city = officeInfo.address?.city as string;
  const officeState = officeInfo.address?.stateCode as string;
  const zip = officeInfo.address?.zipCode as string;
  const reviews = officeInfo.reviews;
  const workingHours = useFacilityBeffeDataSource ? getWorkingHoursFromBff((officeInfo.workingHours ?? []) as BffWorkingHours[], true) : getWorkingHours(officeInfo.workingHours ?? [], true);
  const weekendAvailabilityMessage = getWeekendAvailabilityMessage(workingHours);
  return <Box width="100%" sx={{
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'flex-start',
    gap: '.5rem'
  }} data-sentry-element="Box" data-sentry-component="OfficeInfo" data-sentry-source-file="OfficeInfo.tsx">
			<TAGButton size="S" variant="tertiaryHC" href={resolveContentVariable(`${googleMapsBusinessQuery ?? config.googleMapBusinessQuery}`, officeInfo as Maybe<Facility>)} target="_blank" sx={addressLinkStyles} data-sentry-element="TAGButton" data-sentry-source-file="OfficeInfo.tsx">
				{address1}
				<br />
				{address2}
				{address2 && <br />}
				{city}, {officeState} {zip}
			</TAGButton>
			{reviews?.reviewCount != null && <ReviewRating averageRating={reviews?.averageRating} reviewCount={reviews?.reviewCount} textColor={isDarkBackground ? 'text.light' : undefined} facilityName={officeInfo.name} facilityCode={officeInfo.code} />}
			{!displayOfficeHoursInModal && !!officeInfo.workingHours?.length && <Box sx={workingHoursBoxStyles}>
					<Typography color="text.light" variant="bodyLargeBold" component="h2">
						Hours of Operation
					</Typography>
					<Grid container gap={1} sx={workingHoursContainerStyles}>
						{groupDays(workingHours).map(item => <Grid container key={item.day} gap={1.5} alignItems="center">
								<Typography color="text.light" variant="bodyLargeBook" width="50%">
									{item.day}:
								</Typography>
								<Typography color="text.light" variant="bodyLargeBook">
									{item.hours}
								</Typography>
							</Grid>)}
					</Grid>
				</Box>}
			{displayOfficeHoursInModal && !!officeInfo.workingHours?.length && <Box sx={officeHoursButtonContainerStyles}>
					<TAGButton dataTestId="service_line_hero_hours_button" size="S" startIcon={<TAGSvgIcon icon="Clock" size={24} />} variant="tertiaryHC" onClick={() => setShowHoursModal(true)}>
						Hours
					</TAGButton>
					{weekendAvailabilityMessage && <Box sx={weekendAvailabilityStyles}>
							<TAGSvgIcon icon="Calendar" size={20} dataTestId="service_line_hero_weekend_availability_icon" />
							<Typography color="text.light" variant="bodyLargeBook">
								{weekendAvailabilityMessage}
							</Typography>
						</Box>}
				</Box>}
			{showHoursModal && !!officeInfo.workingHours?.length && <OfficeHours handleClose={() => setShowHoursModal(false)} workingHours={officeInfo.workingHours} useFacilityBeffeDataSource={useFacilityBeffeDataSource} officeHoursLabel={officeHoursLabel} />}
		</Box>;
};
export default OfficeInfo;